<script setup lang="ts">
  import { onMounted, onBeforeUnmount, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { useNotification, Button } from "@ui-kit";
  import { useBarcode, usePowerRfid, useReadRfid, useWriteRfid, LOCK_MEM_TYPE, PASSWORD_FOR_WRITE_EPC } from "@plugins";
  import { WTFIRInputType, useSetPairs, setPairs as setPairsApi, ResponseCode } from "@src/api";
  import { useProfileOptions } from "@src/hooks";
  import { ProductShortCard } from "@src/components";
  import { useCheckPairs } from "./useCheckPairs";
  import { default as CheckPairsSecondStepFooter } from "./CheckPairsSecondStepFooter.vue";

  const emit = defineEmits<{ (e: "close"): void; (e: "click:back"): void }>();

  const barcode = useBarcode();
  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();
  const writeRfid = useWriteRfid();
  const { show } = useNotification();
  const { t } = useI18n();
  const { mutateAsync: setPairs } = useSetPairs();
  const { profile, hasPermission } = useProfileOptions();
  const { data, steps } = useCheckPairs();

  const state = ref<{ stage: string; action?: string }>({ stage: "initial" });

  const scanDataSecondStep = async (inputScanData: string, inputScanType: WTFIRInputType) => {
    if (data.isLoading.value || !inputScanData) return;

    data.set({ [inputScanType]: { value: inputScanData } });
    await data.load(inputScanType);

    if (!data.validation()) {
      state.value = { stage: "error" };

      return;
    }

    const checkResult = data.check();

    state.value = { stage: checkResult.stage, action: checkResult.action };

    if (checkResult.message) {
      show({ title: checkResult.message, type: checkResult.type as "success" | "error" | "warning" });
    }

    if (checkResult.stage === "success") {
      handleRemoveListener();

      if (data.value.value.pair) {
        setPairs({ pairs: [{ epc: data.value.value.pair.epc, cis: data.value.value.pair.cis }] });
      }
    }
  };

  const handleAddListeners = () => {
    if (data.value.value.rfid) {
      barcode.subscribe((inputBarcodeData) => scanDataSecondStep(inputBarcodeData, WTFIRInputType.barcode));
    } else {
      readRfid.subscribe((inputRfidData) => scanDataSecondStep(inputRfidData.value, WTFIRInputType.rfid));
    }
  };

  const handleRemoveListener = () => {
    readRfid.unsubscribe();
    barcode.unsubscribe();
  };

  const handleSetPairs = async () => {
    try {
      if (data.value.value.barcode.value && data.value.value.rfid.value) {
        const result = await setPairsApi({
          pairs: [
            {
              epc: data.value.value.rfid.value,
              cis: data.value.value.barcode.value,
            },
          ],
        });

        if (result.code === ResponseCode.OK) {
          show({ title: t("Пара успешно установлена"), type: "success" });
          state.value = { stage: "success" };
        }
      }
    } catch (error) {
      if (error.message) {
        show({ title: error.message, type: "error" });
      }

      if (error.code === 406) {
        steps.setQuarantine(true);
      }
    }
  };

  const handleWritePair = async () => {
    if (profile.value?.constants.rfid.power_write) {
      await powerRfid.set(profile.value.constants.rfid.power_write);
    }

    const resultWriteRfidData = await writeRfid.writeData({ epc: data.value.value.pair.epc, filterData: data.value.value.rfid.value });

    if (!resultWriteRfidData) {
      show({ title: t("Ошибка записи EPC"), type: "error" });
      return;
    }

    const resultWriteRfidPassword = await writeRfid.writePassword({
      newPassword: PASSWORD_FOR_WRITE_EPC,
      filterData: data.value.value.pair.epc,
    });

    if (!resultWriteRfidPassword) {
      show({ title: t("Ошибка записи буфера"), type: "error" });
      return;
    }

    const resultLockMem = await writeRfid.lockMem({
      password: PASSWORD_FOR_WRITE_EPC,
      filterData: data.value.value.pair.epc,
      type: LOCK_MEM_TYPE.ALL,
    });

    if (!resultLockMem) {
      show({ title: t("Ошибка блокировки памяти"), type: "error" });
      return;
    }

    show({ title: t("Успешная запись EPC"), type: "success" });

    state.value = { stage: "initial" };

    handleAddListeners();
  };

  const handleGoToQuarantine = () => {
    steps.setQuarantine(true);

    handleRemoveListener();
  };

  onMounted(() => {
    handleAddListeners();
  });

  onBeforeUnmount(() => {
    handleRemoveListener();
  });
</script>

<template>
  <div class="card-page-short" data-testid="card-page-short">
    <ProductShortCard :productId="data.value.value.product?.id" @click:back="emit('close')" />
    <CheckPairsSecondStepFooter
      :isRfid="data.value.value.isFirstScannedRfid"
      :state="state.stage"
      @click:ok="handleGoToQuarantine"
      @click:back="emit('click:back')"
      @close="emit('close')"
    >
      <template #pair_write>
        <Button
          v-if="hasPermission('mobile.pair_write') && state.action === 'epc-write'"
          style="padding: 14px 34px"
          @click="handleWritePair"
          data-testid="write-pair-button"
        >
          {{ t("Записать") }}
        </Button>
      </template>
      <template #pair_set>
        <Button
          v-if="hasPermission('mobile.pair_set') && state.action === 'set-pair'"
          style="padding: 14px 34px"
          @click="handleSetPairs"
          data-testid="set-pair-button"
        >
          {{ t("Спарить") }}
        </Button>
      </template>
    </CheckPairsSecondStepFooter>
  </div>
</template>

<style scoped>
  .card-page-short {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: var(--sf-base-background-card);
  }
</style>
